/**
 * @module ProviderRadio
 * @purpose Radio group switching the demo AI briefing route between the self-hosted
 *   (primary) and externally hosted (alternate) provider profile.
 * @depends contexts/demo-mode-context (useDemoMode), services/platform (setDemoLlmProvider)
 * @dependedOnBy pages/platform-settings/DemoModeCard
 *
 * @rules
 * - Performance: one in-flight request at a time; no polling of its own -- reads the
 *   current provider from the shared DemoModeContext
 * - State: local submitting/errorCode only; the selected provider itself lives in context
 * - Errors: ALTERNATE_PROFILE_NOT_CONFIGURED and DEMO_MODE_REQUIRED map to translated,
 *   inline copy -- the raw backend code is never rendered
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useDemoMode } from '../../contexts/demo-mode-context'
import { setDemoLlmProvider, getDemoModeErrorCode } from '../../services/platform'
import { DemoModeErrorCodes } from '../../types/platform'
import type { DemoLlmProvider } from '../../types/platform'

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '8px',
}

const OPTION_LABEL_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'var(--font-body)',
  fontSize: '0.875rem',
  color: 'var(--text-dim)',
  marginBottom: '6px',
}

const ERROR_TEXT_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  color: 'rgb(220, 38, 38)',
  fontSize: '0.8rem',
  marginTop: '6px',
}

function errorMessageFor(code: string | null, t: TFunction<'admin'>): string | null {
  if (code === null) {
    return null
  }
  if (code === DemoModeErrorCodes.ALTERNATE_PROFILE_NOT_CONFIGURED) {
    return t('demoMode.error.alternateProfileNotConfigured')
  }
  if (code === DemoModeErrorCodes.DEMO_MODE_REQUIRED) {
    return t('demoMode.error.demoModeRequired')
  }
  return t('demoMode.error.generic')
}

/**
 * Provider profile radio group for the platform admin settings page.
 *
 * @behaviour Reads the current provider from `DemoModeContext`; on selection change,
 *            calls `setDemoLlmProvider` and refetches the shared status on success.
 * @errors Failures render translated copy inline and leave the selection unchanged.
 */
export function ProviderRadio(): React.ReactElement {
  const { t } = useTranslation('admin')
  const { status, refetch } = useDemoMode()
  const [submitting, setSubmitting] = useState(false)
  const [errorCode, setErrorCode] = useState<string | null>(null)

  const currentProvider: DemoLlmProvider = status?.demoMode.llmProvider ?? 'primary'

  const handleSelect = async (provider: DemoLlmProvider): Promise<void> => {
    if (submitting || provider === currentProvider) {
      return
    }
    setSubmitting(true)
    setErrorCode(null)
    try {
      await setDemoLlmProvider(provider)
      await refetch()
    } catch (err) {
      setErrorCode(getDemoModeErrorCode(err) ?? 'unknown')
    } finally {
      setSubmitting(false)
    }
  }

  const errorMessage = errorMessageFor(errorCode, t)

  return (
    <div role="radiogroup" aria-label={t('demoMode.provider.title')}>
      <p style={TITLE_STYLE}>{t('demoMode.provider.title')}</p>
      <label style={OPTION_LABEL_STYLE}>
        <input
          type="radio"
          name="demo-llm-provider"
          checked={currentProvider === 'primary'}
          disabled={submitting}
          onChange={() => {
            void handleSelect('primary')
          }}
        />
        {t('demoMode.provider.primary')}
      </label>
      <label style={OPTION_LABEL_STYLE}>
        <input
          type="radio"
          name="demo-llm-provider"
          checked={currentProvider === 'alternate'}
          disabled={submitting}
          onChange={() => {
            void handleSelect('alternate')
          }}
        />
        {t('demoMode.provider.alternate')}
      </label>
      {errorMessage !== null && (
        <p role="alert" style={ERROR_TEXT_STYLE}>
          {errorMessage}
        </p>
      )}
    </div>
  )
}
